import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth, ROLES } from './contexts/AuthContext';
import ProtectedRoute, { AdminRoute, SuperAdminRoute } from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import DashboardLayout from './layouts/DashboardLayout';
import ResidentLayout from './layouts/ResidentLayout';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import KokpitPage from './pages/KokpitPage';
import DeviceManagerPage from './pages/DeviceManagerPage';
import UsersPage from './pages/UsersPage';
import SitesPage from './pages/SitesPage';
import StationsPage from './pages/StationsPage';
import SessionsPage from './pages/SessionsPage';
import RFIDPage from './pages/RFIDPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import OCPPPage from './pages/OCPPPage';
import EnergyPage from './pages/EnergyPage';
import LicensesPage from './pages/LicensesPage';
import LicensePaymentPage from './pages/LicensePaymentPage';
import LicenseDistributionPage from './pages/LicenseDistributionPage';
import PaymentApprovalsPage from './pages/PaymentApprovalsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import BackupsPage from './pages/BackupsPage';
import SchedulerPage from './pages/SchedulerPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import FaultManagementPage from './pages/FaultManagementPage';
import DocumentationPage from './pages/DocumentationPage';
import TariffsPage from './pages/TariffsPage';
import NotFoundPage from './pages/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import ModuleManagementPage from './pages/ModuleManagementPage';
import InvoiceAnalysisPage from './pages/InvoiceAnalysisPage';
import HaritaPage from './pages/HaritaPage';
import ManagementPage from './pages/ManagementPage';
import LocationsPage from './pages/LocationsPage';
import TechnicianManagementPage from './pages/TechnicianManagementPage';
import TechnicianPortalPage from './pages/TechnicianPortalPage';
import TechnicianRegisterPage from './pages/TechnicianRegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import ContentManagementPage from './pages/ContentManagementPage';
import BoardPage from './pages/BoardPage';
import WorkOrdersPage from './pages/WorkOrdersPage';
import TechnicianPage from './pages/TechnicianPage';
import MembershipPaymentPage from './pages/MembershipPaymentPage';

// Resident Pages
import ResidentDashboardPage from './pages/resident/ResidentDashboardPage';
import ResidentChargingHistoryPage from './pages/resident/ResidentChargingHistoryPage';
import ResidentBalancePage from './pages/resident/ResidentBalancePage';
import ResidentProfilePage from './pages/resident/ResidentProfilePage';
import ResidentChargingPage from './pages/resident/ResidentChargingPage';
import ResidentReservationPage from './pages/resident/ResidentReservationPage';

// Yardımcı Pages
import YardimciDashboardPage from './pages/yardimci/YardimciDashboardPage';

// ========== LAYOUT WRAPPERS (Route dışında kalarak remount'u önler) ==========

// Dashboard Layout Wrapper - Tek bir DashboardLayout instance'ı
const DashboardLayoutWrapper = () => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  // Site Sakini ise resident dashboard'a yönlendir
  if (user?.role === ROLES.SITE_SAKINI || user?.role === 'site_sakini' || user?.role === 'user') {
    return <Navigate to="/resident/dashboard" replace />;
  }
  
  // Yardımcı ise yardimci dashboard'a yönlendir  
  if (user?.role === ROLES.YARDIMCI || user?.role === 'yardimci') {
    return <Navigate to="/yardimci/dashboard" replace />;
  }
  
  // Teknisyen (usta) rolü ise teknisyen paneline yönlendir
  // NOT: calisan rolü artık normal dashboard'a gider (Çalışan/Şoför menüleri için)
  const technicianRoles = ['hims_team', 'subcontractor', 'service_company', 'technician'];
  if (technicianRoles.includes(user?.role) || technicianRoles.includes(user?.technician_type)) {
    // Admin değilse teknisyen paneline yönlendir
    if (!['super_admin', 'admin', 'hesap_yoneticisi', 'site_manager'].includes(user?.role)) {
      return <Navigate to="/technician" replace />;
    }
  }
  
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </ProtectedRoute>
  );
};

// Admin Layout Wrapper - Admin kontrolü ile
const AdminLayoutWrapper = () => {
  return (
    <AdminRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </AdminRoute>
  );
};

// Super Admin Layout Wrapper
const SuperAdminLayoutWrapper = () => {
  return (
    <SuperAdminRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </SuperAdminRoute>
  );
};

// Content Manager Layout Wrapper - icerik_yoneticisi, admin, super_admin
const ContentManagerLayoutWrapper = () => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  const allowedRoles = ['super_admin', 'admin', 'icerik_yoneticisi'];
  const hasAccess = allowedRoles.includes(user?.role);
  
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </ProtectedRoute>
  );
};

// Technician Layout Wrapper
const TechnicianLayoutWrapper = () => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  const allowedRoles = ['super_admin', 'admin', 'hims_team', 'subcontractor', 'service_company', 'technician', 'calisan'];
  const userRole = user?.role;
  const technicianType = user?.technician_type;
  const hasAccess = allowedRoles.includes(userRole) || allowedRoles.includes(technicianType);
  
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  );
};

// Resident Layout Wrapper
const ResidentLayoutWrapper = () => {
  return (
    <ProtectedRoute>
      <ResidentLayout>
        <Outlet />
      </ResidentLayout>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
        <Routes>
          {/* ========== PUBLIC ROUTES ========== */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/register-technician" element={<TechnicianRegisterPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/membership-payment" element={<MembershipPaymentPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          
          {/* ========== DASHBOARD LAYOUT ROUTES (Tek Layout Instance) ========== */}
          <Route element={<DashboardLayoutWrapper />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/kokpit" element={<KokpitPage />} />
            <Route path="/device-manager" element={<DeviceManagerPage />} />
            <Route path="/sessions" element={<SessionsPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/notification-settings" element={<NotificationSettingsPage />} />
            <Route path="/documentation" element={<DocumentationPage />} />
            <Route path="/yardimci/dashboard" element={<YardimciDashboardPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:id" element={<NewsDetailPage />} />
            <Route path="/board" element={<BoardPage />} />
          </Route>
          
          {/* ========== CONTENT MANAGER LAYOUT ROUTES ========== */}
          <Route element={<ContentManagerLayoutWrapper />}>
            <Route path="/content-management" element={<ContentManagementPage />} />
          </Route>
          
          {/* ========== ADMIN LAYOUT ROUTES ========== */}
          <Route element={<AdminLayoutWrapper />}>
            <Route path="/users" element={<UsersPage />} />
            <Route path="/sites" element={<SitesPage />} />
            <Route path="/stations" element={<StationsPage />} />
            <Route path="/work-orders" element={<WorkOrdersPage />} />
            <Route path="/rfid" element={<RFIDPage />} />
            <Route path="/faults" element={<FaultManagementPage />} />
            <Route path="/ocpp" element={<OCPPPage />} />
            <Route path="/energy" element={<EnergyPage />} />
            <Route path="/licenses" element={<LicensesPage />} />
            <Route path="/licenses/:licenseId/payment" element={<LicensePaymentPage />} />
            <Route path="/license-distribution" element={<LicenseDistributionPage />} />
            <Route path="/tariffs" element={<TariffsPage />} />
            <Route path="/map" element={<HaritaPage />} />
            <Route path="/audit-logs" element={<AuditLogsPage />} />
            <Route path="/modules" element={<ModuleManagementPage />} />
            <Route path="/invoice-analysis" element={<InvoiceAnalysisPage />} />
            <Route path="/management" element={<ManagementPage />} />
            <Route path="/locations" element={<LocationsPage />} />
            <Route path="/technicians" element={<TechnicianManagementPage />} />
          </Route>
          
          {/* ========== SUPER ADMIN LAYOUT ROUTES ========== */}
          <Route element={<SuperAdminLayoutWrapper />}>
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/payment-approvals" element={<PaymentApprovalsPage />} />
            <Route path="/scheduler" element={<SchedulerPage />} />
            <Route path="/backups" element={<BackupsPage />} />
          </Route>
          
          {/* ========== TECHNICIAN LAYOUT ROUTES ========== */}
          <Route element={<TechnicianLayoutWrapper />}>
            <Route path="/technician" element={<TechnicianPage />} />
            <Route path="/technician-portal" element={<TechnicianPortalPage />} />
            <Route path="/technician/news" element={<NewsPage />} />
            <Route path="/technician/news/:id" element={<NewsDetailPage />} />
          </Route>
          
          {/* ========== RESIDENT LAYOUT ROUTES ========== */}
          <Route element={<ResidentLayoutWrapper />}>
            <Route path="/resident/dashboard" element={<ResidentDashboardPage />} />
            <Route path="/resident/charging" element={<ResidentChargingPage />} />
            <Route path="/resident/reservations" element={<ResidentReservationPage />} />
            <Route path="/resident/charging-history" element={<ResidentChargingHistoryPage />} />
            <Route path="/resident/balance" element={<ResidentBalancePage />} />
            <Route path="/resident/profile" element={<ResidentProfilePage />} />
          </Route>
          
          {/* ========== DEFAULT REDIRECTS ========== */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        
        <Toaster position="top-right" richColors />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
